function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createNumberInput({ id = '', className = 'cost-input', value, step, data = {} }) {
  const input = createElement('input', className);
  if (id) input.id = id;
  input.type = 'number';
  input.min = '0';
  input.step = step;
  input.value = String(Number.isFinite(Number(value)) ? value : 0);
  Object.entries(data).forEach(([key, item]) => {
    input.dataset[key] = String(item ?? '');
  });
  return input;
}

function createField(labelText, control) {
  const field = createElement('div', 'cost-field');
  const label = createElement('label', '', labelText);
  if (control.id) label.htmlFor = control.id;
  field.append(label, control);
  return field;
}

function createFormulaBox(text) {
  const box = createElement('div', 'cost-formula-box');
  box.append(createElement('span', '', '💡'), createElement('span', '', text));
  return box;
}

function createConfigCard(title, ...children) {
  const card = createElement('div', 'cost-config-card');
  card.append(createElement('div', 'cost-config-title', title), ...children);
  return card;
}

function createTab(key, label, active = false) {
  const tab = createElement('button', `cost-tab${active ? ' is-active' : ''}`, label);
  tab.type = 'button';
  tab.dataset.costTab = key;
  tab.setAttribute('role', 'tab');
  tab.setAttribute('aria-selected', active ? 'true' : 'false');
  return tab;
}

function createPanel(key, active = false) {
  const panel = createElement('div', `cost-tab-panel${active ? ' is-active' : ''}`);
  panel.dataset.costPanel = key;
  panel.hidden = !active;
  return panel;
}

function createProviderCard({ title, key, config }) {
  const card = createElement('div', 'cost-provider-card');
  const cycle = createElement('select', 'cost-select');
  cycle.id = `cost-${key}-cycle`;
  [['month', '按月'], ['year', '按年']].forEach(([value, label]) => {
    const option = createElement('option', '', label);
    option.value = value;
    option.selected = config.billingCycle === value;
    cycle.append(option);
  });
  const formula = createElement('div', 'cost-calc-strip');
  formula.id = `cost-${key}-formula`;
  card.append(
    createElement('div', 'cost-provider-title', title),
    createField('计费方式', cycle),
    createField('充值总费用（RMB）', createNumberInput({ id: `cost-${key}-fee`, value: config.totalFee, step: '0.01' })),
    createField('周期可用次数', createNumberInput({ id: `cost-${key}-calls`, value: config.totalCalls, step: '1' })),
    createField('上期结余', createNumberInput({ id: `cost-${key}-balance`, value: config.openingBalance, step: '1' })),
    formula,
  );
  return card;
}

function createModelConfiguration({ modelCandidates, modelRates, defaultModelOption, formatCurrency }) {
  const target = createElement('select', 'cost-select');
  target.id = 'cost-model-target';
  modelCandidates.forEach((item) => {
    const value = item.value;
    const option = createElement('option', '', `${item.label || item.name}（${item.provider}）`);
    option.value = value;
    option.selected = value === defaultModelOption;
    target.append(option);
  });
  const targetField = createField('当前估算模型', target);
  targetField.classList.add('cost-model-target-field');

  const table = createElement('table', 'cost-table');
  const thead = document.createElement('thead');
  const header = document.createElement('tr');
  ['模型名称', '供应商', '输入价格（RMB / 百万 tokens）', '输出价格（RMB / 百万 tokens）', '示例：100万输入 + 100万输出']
    .forEach((label) => header.append(createElement('th', '', label)));
  thead.append(header);
  const tbody = document.createElement('tbody');
  modelCandidates.forEach((item) => {
    const config = modelRates[item.name] || {};
    const inputPerMillion = Number(config.inputPerMillion || 0);
    const outputPerMillion = Number(config.outputPerMillion || 0);
    const row = document.createElement('tr');
    row.dataset.modelKey = item.value;
    row.dataset.modelName = item.name || '';
    row.dataset.modelProvider = item.provider || '';
    const input = createNumberInput({
      className: 'cost-input cost-model-input',
      value: inputPerMillion,
      step: '0.0001',
      data: { modelKey: item.value },
    });
    const output = createNumberInput({
      className: 'cost-input cost-model-output',
      value: outputPerMillion,
      step: '0.0001',
      data: { modelKey: item.value },
    });
    const inputCell = document.createElement('td');
    inputCell.append(input);
    const outputCell = document.createElement('td');
    outputCell.append(output);
    row.append(
      createElement('td', 'cost-mono', item.name || ''),
      createElement('td', '', item.provider || ''),
      inputCell,
      outputCell,
      createElement('td', 'cost-mono', formatCurrency(inputPerMillion + outputPerMillion)),
    );
    tbody.append(row);
  });
  const scroll = createElement('div', 'cost-table-scroll');
  table.append(thead, tbody);
  scroll.append(table);
  return createConfigCard(
    '模型费用配置',
    createElement('p', 'cost-subtitle', '当前费用估算默认使用 MiniMax M3。保留其他模型价格配置，方便后续切换或历史成本核对。'),
    targetField,
    scroll,
    createFormulaBox('模型成本 = 输入 tokens / 1,000,000 × 输入价格 + 输出 tokens / 1,000,000 × 输出价格'),
  );
}

function createConfigurationSection({ image, marketTools, modelCandidates, modelRates, defaultModelOption, formatCurrency }) {
  const section = createElement('section', 'cost-section-block');
  const titleGroup = document.createElement('div');
  titleGroup.append(createElement('h3', '', '费用配置'), createElement('span', '', '按模块配置计费参数'));
  const titleRow = createElement('div', 'cost-section-title-row');
  titleRow.append(titleGroup);
  section.append(titleRow);

  const tabs = createElement('div', 'cost-tab-bar');
  tabs.setAttribute('role', 'tablist');
  tabs.setAttribute('aria-label', '费用配置类型');
  tabs.append(createTab('image', '📸 图片算力', true), createTab('market', '🧭 市场工具'), createTab('model', '🤖 模型费用'));

  const imagePanel = createPanel('image', true);
  const imageGrid = createElement('div', 'cost-form-grid');
  imageGrid.append(
    createField('充值总费用（RMB）', createNumberInput({ id: 'cost-image-fee', value: image.totalFee, step: '0.01' })),
    createField('购买量（算力）', createNumberInput({ id: 'cost-image-units', value: image.totalUnits, step: '1' })),
  );
  const imageFormula = createElement('div', 'cost-calc-strip');
  imageFormula.id = 'cost-image-formula';
  imagePanel.append(createConfigCard(
    'LinkFox 算力费用配置',
    imageGrid,
    imageFormula,
    createFormulaBox('图片算力单次成本 = 充值总费用 / 购买量'),
  ));

  const marketPanel = createPanel('market');
  const providerGrid = createElement('div', 'cost-provider-grid');
  providerGrid.append(
    createProviderCard({ title: '卖家精灵', key: 'sellersprite', config: marketTools.sellersprite }),
    createProviderCard({ title: '数派', key: 'sorftime', config: marketTools.sorftime }),
  );
  marketPanel.append(createConfigCard(
    '市场工具费用配置',
    createElement('p', 'cost-subtitle', '按市场工具供应商分列配置。单次成本 = 本周期费用 / (周期可用次数 + 上期结余)。'),
    providerGrid,
  ));

  const modelPanel = createPanel('model');
  modelPanel.append(createModelConfiguration({ modelCandidates, modelRates, defaultModelOption, formatCurrency }));
  section.append(tabs, imagePanel, marketPanel, modelPanel);
  return section;
}

function createUsageSection() {
  const section = createElement('section', 'cost-section-block');
  const titleGroup = document.createElement('div');
  titleGroup.append(
    createElement('h3', '', '用户消耗明细'),
    createElement('span', '', '展示所有用户，支持搜索、批量设置 LinkFox / LLM / 市场工具限额'),
  );
  const titleRow = createElement('div', 'cost-section-title-row');
  titleRow.append(titleGroup);
  const topUsers = createElement('div');
  topUsers.id = 'cost-calc-top-users';
  const label = createElement('div', 'cost-limit-subsection-label', '用户限额管理');
  const search = createElement('input', 'search-input');
  search.id = 'cost-limit-search';
  search.type = 'text';
  search.placeholder = '搜索用户名或邮箱...';
  const searchButton = createElement('button', 'btn btn-primary', '筛选');
  searchButton.id = 'cost-limit-search-btn';
  searchButton.type = 'button';
  const batchButton = createElement('button', 'btn btn-secondary', '批量设置');
  batchButton.id = 'cost-limit-batch-btn';
  batchButton.type = 'button';
  const toolbar = createElement('div', 'toolbar cost-limit-toolbar');
  const toolbarLeft = createElement('div', 'toolbar-left');
  const toolbarRight = createElement('div', 'toolbar-right');
  toolbarLeft.append(search, searchButton);
  toolbarRight.append(batchButton);
  toolbar.append(toolbarLeft, toolbarRight);
  const tables = createElement('div');
  tables.id = 'cost-calc-estimate-tables';
  const pagination = createElement('div');
  pagination.id = 'cost-limit-pagination';
  const caption = createElement('div', 'cost-table-caption');
  caption.id = 'cost-calc-estimate-caption';
  section.append(titleRow, topUsers, label, toolbar, tables, pagination, caption);
  return section;
}

export function renderCostCalculationConfiguration(root, options) {
  if (!root) return;
  root.replaceChildren(createConfigurationSection(options), createUsageSection());
}
